import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg, goUrl, inr, fmtDate } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { assetUrl, useSite } from "@/lib/site";
import { CategoryIcon } from "@/lib/categoryIcons";
import { Crumbs, CouponCard, Empty } from "@/components/site/bits";
import { DealGrid, DemoBadge } from "@/components/site/DealCard";
import { useCompare } from "@/components/site/compare";

const wrap = "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10";
const H1 = ({ children, testid }) => <h1 data-testid={testid} className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-8">{children}</h1>;

export function Coupons() {
  useSeo({ title: "Coupon codes & offers", description: "Working coupon codes and bank offers for Indian stores, with clear terms and validity dates." });
  const { data } = useQuery({ queryKey: ["coupons"], queryFn: () => api.get("/public/coupons").then((r) => r.data) });
  return (
    <div className={wrap} data-testid="coupons-page">
      <Crumbs items={[["/", "Home"], [null, "Coupons"]]} />
      <H1>Coupon codes & offers</H1>
      <div className="grid md:grid-cols-2 gap-4">{(data || []).map((c) => <CouponCard key={c.id} c={c} />)}</div>
      {data && !data.length && <Empty text="No active coupons right now." />}
    </div>
  );
}

const Pill = ({ n, one, many, cls, testid }) => n > 0 && (
  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={testid}>{n} {n === 1 ? one : many}</span>
);

const TileThumb = ({ x }) => x.logo_url
  ? <img src={assetUrl(x.logo_url)} alt="" loading="lazy" className="h-10 w-10 rounded-lg object-contain bg-white border border-slate-100 shrink-0" />
  : <CategoryIcon icon={x.icon} name={x.name} className="h-10 w-10" size={20} />;

const Tiles = ({ items, base, testid }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={testid}>
    {items.map((x) => (
      <Link key={x.id} to={`${base}/${x.slug}`} data-testid={`tile-${x.slug}`} className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 hover:border-emerald-500 hover:shadow-md transition-[box-shadow,border-color] flex flex-col gap-3">
        <span className="flex items-center gap-3 min-w-0">
          <TileThumb x={x} />
          <span className="font-display font-extrabold text-base sm:text-lg leading-tight line-clamp-2 min-w-0 break-words">{x.name}</span>
        </span>
        <span className="flex flex-wrap gap-1.5">
          <Pill n={x.deal_count} one="live deal" many="live deals" cls="bg-emerald-50 text-emerald-700" testid={`tile-${x.slug}-deals`} />
          <Pill n={x.coupon_count} one="coupon" many="coupons" cls="bg-amber-50 text-amber-800" testid={`tile-${x.slug}-coupons`} />
          {!x.deal_count && !x.coupon_count && <span className="text-xs text-slate-400">No live deals</span>}
        </span>
      </Link>
    ))}
  </div>
);

export function Stores() {
  useSeo({ title: "Stores", description: "Deals and coupons by store: Amazon, Flipkart, Myntra, Ajio, Meesho, Nykaa and more." });
  const { data } = useQuery({ queryKey: ["merchants"], queryFn: () => api.get("/public/merchants").then((r) => r.data) });
  return <div className={wrap} data-testid="stores-page"><Crumbs items={[["/", "Home"], [null, "Stores"]]} /><H1>Shop by store</H1><Tiles items={data || []} base="/store" testid="stores-grid" /></div>;
}

export function Categories() {
  useSeo({ title: "Categories", description: "Browse verified deals by category." });
  const { data } = useQuery({ queryKey: ["cats"], queryFn: () => api.get("/public/categories").then((r) => r.data) });
  const parents = (data || []).filter((c) => !c.parent_id);
  return (
    <div className={wrap} data-testid="categories-page">
      <Crumbs items={[["/", "Home"], [null, "Categories"]]} />
      <H1>Categories</H1>
      <div className="space-y-10">
        {parents.map((p) => (
          <section key={p.id}>
            <Link to={`/category/${p.slug}`} className="inline-flex items-center gap-3 text-xl font-bold hover:text-emerald-700" data-testid={`category-head-${p.slug}`}>
              <CategoryIcon icon={p.icon} name={p.name} className="h-9 w-9" size={18} /> {p.name} →
            </Link>
            <div className="mt-4"><Tiles items={data.filter((c) => c.parent_id === p.id)} base="/category" testid={`subcats-${p.slug}`} /></div>
          </section>
        ))}
      </div>
    </div>
  );
}

export function Guides() {
  useSeo({ title: "Buying guides & articles", description: "Practical, honest buying guides for Indian shoppers." });
  const { data } = useQuery({ queryKey: ["posts"], queryFn: () => api.get("/public/posts").then((r) => r.data) });
  return (
    <div className={wrap} data-testid="guides-page">
      <Crumbs items={[["/", "Home"], [null, "Guides"]]} />
      <H1>Buying guides</H1>
      <div className="grid md:grid-cols-3 gap-6">
        {(data || []).map((g) => (
          <Link key={g.id} to={`/guide/${g.slug}`} data-testid={`guide-card-${g.slug}`} className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
            {g.cover_url && <img src={g.cover_url} alt="" className="h-44 w-full object-cover" loading="lazy" />}
            <div className="p-5 space-y-1"><span className="text-xs font-bold uppercase text-amber-700">{g.kind}</span><p className="font-bold group-hover:text-emerald-700">{g.title}</p><p className="text-sm text-slate-500 line-clamp-2">{g.excerpt}</p></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export const Markdown = ({ text }) => (
  <div className="prose-br">
    {(text || "").split(/\n\n+/).flatMap((b) => (b.startsWith("## ") && b.includes("\n") ? [b.slice(0, b.indexOf("\n")), b.slice(b.indexOf("\n") + 1)] : [b])).map((block, i) =>
      block.startsWith("## ") ? <h2 key={i}>{block.slice(3)}</h2>
        : <p key={i} dangerouslySetInnerHTML={{ __html: block.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\*(.+?)\*/g, "<em>$1</em>") }} />)}
  </div>
);

export function GuidePage() {
  const { slug } = useParams();
  const { data, isError } = useQuery({ queryKey: ["post", slug], queryFn: () => api.get(`/public/posts/${slug}`).then((r) => r.data) });
  useSeo({ title: data?.seo_title || data?.title, description: data?.meta_description || data?.excerpt,
    jsonLd: data && { "@context": "https://schema.org", "@type": "Article", headline: data.title, author: { "@type": "Organization", name: data.author }, datePublished: data.created_at } });
  if (isError) return <div className={wrap}>Article not found.</div>;
  if (!data) return <div className={wrap}><div className="h-64 bg-slate-200 rounded-xl animate-pulse" /></div>;
  return (
    <article className="max-w-3xl mx-auto px-4 py-10" data-testid="guide-page">
      <Crumbs items={[["/", "Home"], ["/guides", "Guides"], [null, data.title]]} />
      <DemoBadge show={data.is_demo} />
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">{data.title}</h1>
      <p className="text-sm text-slate-500 mt-2">By {data.author} · {fmtDate(data.created_at)}</p>
      {data.cover_url && <img src={data.cover_url} alt="" className="rounded-2xl w-full h-72 object-cover my-8" />}
      <Markdown text={data.body} />
      {data.deals?.length > 0 && <div className="mt-12"><h2 className="text-2xl font-bold mb-6">Deals mentioned</h2><DealGrid deals={data.deals} /></div>}
    </article>
  );
}

export function Compare() {
  const { ids, toggle, clear } = useCompare();
  useSeo({ title: "Compare deals", description: "Compare prices and discounts side by side.", noindex: true });
  const { data } = useQuery({ queryKey: ["compare", ids.join()], enabled: ids.length > 0, queryFn: () => api.get(`/public/compare?ids=${ids.join(",")}`).then((r) => r.data) });
  const rows = [["Store", (d) => d.merchant_name], ["Price", (d) => inr(d.price)], ["MRP", (d) => inr(d.mrp)], ["Discount", (d) => `${d.discount_pct}%`],
    ["You save", (d) => (d.mrp ? inr(d.mrp - d.price) : "—")], ["Score", (d) => `${d.score}/100`], ["Coupon", (d) => d.coupon_code || "—"], ["Price checked", (d) => fmtDate(d.price_updated_at)]];
  return (
    <div className={wrap} data-testid="compare-page">
      <Crumbs items={[["/", "Home"], [null, "Compare"]]} />
      <H1>Compare deals</H1>
      {!ids.length || !data?.length ? <Empty text="Tap the scale icon on any deal card to add up to 4 deals here." testid="compare-empty" /> : (
        <div className="overflow-x-auto bg-white border border-slate-200 rounded-xl">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead><tr><th className="p-4 w-32" />{data.map((d) => (
              <th key={d.id} className="p-4 text-left align-top min-w-[200px]">
                <img src={d.image_url} alt="" className="h-28 w-full object-cover rounded-lg mb-2" />
                <Link to={`/deal/${d.slug}`} className="font-semibold hover:text-emerald-700 line-clamp-2">{d.title}</Link>
                <button onClick={() => toggle(d.id)} className="text-xs text-red-600 mt-1" data-testid="compare-remove-button">Remove</button>
              </th>))}</tr></thead>
            <tbody>
              {rows.map(([label, fn]) => <tr key={label} className="border-t border-slate-100"><td className="p-4 font-semibold text-slate-500">{label}</td>{data.map((d) => <td key={d.id} className="p-4">{fn(d)}</td>)}</tr>)}
              <tr className="border-t border-slate-100"><td />{data.map((d) => <td key={d.id} className="p-4"><a href={goUrl(d.id, "compare")} target="_blank" rel="sponsored nofollow noopener" className="bg-emerald-600 text-white font-bold px-4 py-2 rounded-lg inline-block">Get Deal</a></td>)}</tr>
            </tbody>
          </table>
          <button onClick={clear} className="m-4 text-sm text-slate-500 underline" data-testid="compare-clear-button">Clear all</button>
        </div>
      )}
    </div>
  );
}

export function Contact() {
  const siteName = useSite().name;
  useSeo({ title: "Contact us", description: `Get in touch with the ${siteName} team.` });
  const [f, setF] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/public/contact", f); setSent(true); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="max-w-xl mx-auto px-4 py-10" data-testid="contact-page">
      <H1>Contact us</H1>
      {sent ? <p className="bg-emerald-50 text-emerald-800 p-4 rounded-xl" data-testid="contact-success">Thanks! We read every message and reply within a few working days.</p> : (
        <form onSubmit={submit} className="space-y-4">
          {[["name", "Your name", "text"], ["email", "Email", "email"]].map(([k, l, t]) => (
            <input key={k} required type={t} placeholder={l} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`contact-${k}-input`} className="w-full h-11 rounded-lg border border-slate-200 px-4" />
          ))}
          <textarea required rows={5} placeholder="Message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message-input" className="w-full rounded-lg border border-slate-200 p-4" />
          <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-6 py-3 rounded-lg" data-testid="contact-submit-button">Send message</button>
        </form>
      )}
    </div>
  );
}
